import { zodResolver } from '@hookform/resolvers/zod'
import { BaseSchemaItem } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { z } from '@repo/ui/lang'
import { Textarea } from '@repo/ui/textarea'
import { isEmpty } from 'lodash'
import { ReactNode, useCallback } from 'react'
import { DefaultValues, Path, useForm, UseFormReturn } from 'react-hook-form'

export function BaseForm<
  T extends Pick<BaseSchemaItem, 'label' | 'instructionsToFillValue' | 'type'>,
>({
  children,
  defaultValues,
  onUpdate,
  schema,
}: {
  children: (formApi: UseFormReturn<T>) => ReactNode
  defaultValues: DefaultValues<T>
  onUpdate: (values: T) => void
  schema: z.Schema<T>
}) {
  const { tc } = useT()

  const formApi = useForm<T>({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(schema),
  })

  const { isDirty, errors, isValid } = formApi.formState

  const isFormDisabled = !isDirty || !isEmpty(errors) || !isValid

  const submit = useCallback(() => {
    if (isFormDisabled) return

    const values = formApi.getValues()

    onUpdate(values)
  }, [isFormDisabled, formApi, onUpdate])

  return (
    <Form {...formApi}>
      <form onSubmit={(e) => void formApi.handleSubmit(submit)(e)} className="space-y-4">
        <FormField
          control={formApi.control}
          name={'label' as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('label', { cfl: true })}</FormLabel>
              <FormControl>
                <Input id="label" placeholder={tc('label', { cfl: true })} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {children(formApi)}

        <FormField
          control={formApi.control}
          name={'instructionsToFillValue' as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {tc('instructionsToFillValue', { cfl: true })} ({tc('optional')})
              </FormLabel>
              <FormControl>
                <Textarea
                  id="instructionsToFillValue"
                  placeholder={tc('instructionsToFillValue', { cfl: true })}
                  className="resize-none"
                  rows={2}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-1 items-center justify-between">
          <Button variant="secondary" onClick={() => formApi.reset()} disabled={isFormDisabled}>
            {tc('reset', { cfl: true })}
          </Button>
          <Button type="submit" disabled={isFormDisabled}>
            {tc('update', { cfl: true })}
          </Button>
        </div>
      </form>
    </Form>
  )
}
